import appLogo from '../appicon.jpeg';
import { resolveImageUrl } from './imageUrl';

export const formatPrintDate = (dateString) => {
  if (!dateString) return "N/A";
  return dateString
    .replace("T", " ")
    .replace("Z", "")
    .substring(0, 16);
};

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const BRAND = {
  primary: '#F57C00',
  primaryDark: '#E65100',
  primaryLight: '#FFF3E0',
  primaryBorder: '#FFB74D',
  primarySoft: '#FFE0B2',
};

const PRINT_STYLES = `
  :root {
    --brand-primary: ${BRAND.primary};
    --brand-primary-dark: ${BRAND.primaryDark};
    --brand-primary-light: ${BRAND.primaryLight};
    --brand-primary-border: ${BRAND.primaryBorder};
    --brand-primary-soft: ${BRAND.primarySoft};
  }
  * { box-sizing: border-box; }
  body {
    font-family: Arial, 'Segoe UI', Tahoma, sans-serif;
    margin: 20px;
    color: #1a1a1a;
    direction: rtl;
    text-align: right;
    background: #fff;
  }
  .header {
    text-align: center;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 3px solid var(--brand-primary);
  }
  .logo { width: 120px; height: auto; margin-bottom: 8px; }
  .section {
    margin-bottom: 30px;
    padding: 0;
    page-break-after: always;
  }
  .section:last-child { page-break-after: auto; }
  .order-meta {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 24px;
  }
  .meta-card {
    border: 2px solid var(--brand-primary-border);
    border-radius: 12px;
    padding: 16px;
    background: #fff;
  }
  .invoice-badge {
    display: inline-block;
    background: var(--brand-primary);
    color: #fff;
    padding: 8px 16px;
    border-radius: 8px;
    font-weight: 700;
    font-size: 1rem;
    margin-bottom: 12px;
  }
  .detail-item {
    margin-bottom: 8px;
    padding-bottom: 8px;
    border-bottom: 1px solid var(--brand-primary-soft);
    font-size: 0.95rem;
  }
  .detail-item:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }
  .detail-item strong { color: var(--brand-primary-dark); }
  .items-section { margin-top: 8px; }
  .item-group {
    margin-top: 20px;
    border: 2px solid var(--brand-primary);
    border-radius: 12px;
    overflow: hidden;
    background: #fff;
  }
  .item-group-title {
    margin: 0;
    padding: 10px 16px;
    background: var(--brand-primary);
    color: #fff;
    font-size: 1.05rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .item-group-body { padding: 12px; }
  .trademark-group {
    margin-bottom: 12px;
    border: 1px solid var(--brand-primary-border);
    border-radius: 8px;
    overflow: hidden;
    background: var(--brand-primary-light);
  }
  .trademark-group:last-child { margin-bottom: 0; }
  .trademark-group-title {
    margin: 0;
    padding: 8px 12px;
    background: var(--brand-primary-soft);
    color: var(--brand-primary-dark);
    font-size: 0.95rem;
    font-weight: 700;
    border-bottom: 1px solid var(--brand-primary-border);
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .group-title-img {
    width: 32px;
    height: 32px;
    object-fit: cover;
    border-radius: 6px;
    flex-shrink: 0;
    background: #fff;
  }
  .item-group-title .group-title-img {
    border: 2px solid rgba(255, 255, 255, 0.85);
  }
  .trademark-group-title .group-title-img {
    width: 28px;
    height: 28px;
    border: 1px solid var(--brand-primary-border);
  }
  .group-title-text { line-height: 1.3; }
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 0;
    background: #fff;
  }
  th, td {
    border: 1px solid var(--brand-primary-border);
    padding: 8px 10px;
    text-align: right;
    font-size: 0.9rem;
  }
  th {
    background: var(--brand-primary) !important;
    color: #fff !important;
    font-weight: 600;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  thead tr { background: var(--brand-primary); }
  tbody tr:nth-child(even) { background: #fffaf5; }
  .order-summary {
    margin-top: 24px;
    display: flex;
    justify-content: flex-end;
  }
  .summary-box {
    min-width: 280px;
    border: 2px solid var(--brand-primary);
    border-radius: 12px;
    overflow: hidden;
  }
  .summary-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 16px;
    border-bottom: 1px solid var(--brand-primary-border);
    font-size: 0.95rem;
  }
  .summary-row:last-child { border-bottom: none; }
  .summary-row.total-row {
    background: var(--brand-primary);
    color: #fff;
    font-weight: 700;
    font-size: 1.1rem;
  }
  .empty-note {
    padding: 16px;
    color: #666;
    font-style: italic;
    text-align: center;
    border: 1px dashed var(--brand-primary-border);
    border-radius: 8px;
  }
  @media print {
    body { margin: 10px; }
    .section { page-break-inside: avoid; }
    .item-group { page-break-inside: avoid; }
    th,
    .item-group-title,
    .summary-row.total-row,
    .invoice-badge {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
`;

const buildPrintHeader = () => `
  <div class="header">
    <img src="${appLogo}" alt="واصلك" class="logo" />
  </div>
`;

/** Opens browser print dialog using a hidden iframe (works after async data loads). */
export const printHtmlDocument = (title, bodyHtml) => {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('title', title);
  Object.assign(iframe.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '0',
    height: '0',
    border: '0',
    opacity: '0',
    pointerEvents: 'none',
  });
  document.body.appendChild(iframe);

  const win = iframe.contentWindow;
  if (!win) {
    document.body.removeChild(iframe);
    throw new Error('تعذر فتح نافذة الطباعة');
  }

  const html = `<!DOCTYPE html>
<html lang="ar" dir="rtl">
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(title)}</title>
    <style>${PRINT_STYLES}</style>
  </head>
  <body>
    ${bodyHtml}
  </body>
</html>`;

  const doc = win.document;
  doc.open();
  doc.write(html);
  doc.close();

  const cleanup = () => {
    if (iframe.parentNode) {
      document.body.removeChild(iframe);
    }
  };

  win.addEventListener('afterprint', cleanup, { once: true });
  setTimeout(cleanup, 60000);

  const triggerPrint = () => {
    win.focus();
    win.print();
  };

  const schedulePrint = (delay = 400) => {
    setTimeout(triggerPrint, delay);
  };

  const logoImg = doc.querySelector('.logo');
  const allImages = Array.from(doc.querySelectorAll('img'));
  const pendingImages = allImages.filter((img) => !img.complete);

  const triggerPrintWhenReady = () => {
    if (pendingImages.length === 0) {
      schedulePrint(400);
      return;
    }

    let settled = 0;
    const onSettled = () => {
      settled += 1;
      if (settled >= pendingImages.length) {
        schedulePrint(150);
      }
    };

    pendingImages.forEach((img) => {
      img.onload = onSettled;
      img.onerror = onSettled;
    });
    schedulePrint(3000);
  };

  if (logoImg && !logoImg.complete) {
    logoImg.onload = triggerPrintWhenReady;
    logoImg.onerror = triggerPrintWhenReady;
    schedulePrint(2000);
  } else {
    triggerPrintWhenReady();
  }
};

const calcLineTotal = (item) => {
  const unitPrice = parseFloat(item.unit_price ?? item.unitPrice ?? 0);
  const qty = item.quantity ?? 0;
  const discountPercent = parseFloat(item.discount ?? 0);
  return (unitPrice * qty).toFixed(2);
};

const calcLUnitTotal = (item) => {
  const discountPercent = parseFloat(item.discount ?? 0);
  const unitPrice = parseFloat(item.unit_price ?? item.unitPrice ?? 0);
  if (discountPercent > 0) {
    const originalPrice = unitPrice / (1 - discountPercent / 100);
    return originalPrice.toFixed(2);
  }
  return unitPrice.toFixed(2);
};

var numberOfAllItems = 0;

const orderItemRow = (item) => {
  const unitPrice = parseFloat(item.unit_price ?? item.unitPrice ?? 0);
  const qty = item.quantity ?? 0;
  numberOfAllItems = numberOfAllItems + qty
  const discountPercent = parseFloat(item.discount ?? 0);
  const name = '';// item.product_name ?? item.productName ?? 'غير متوفر';
  const nameAr = item.product_name_arabic ?? item.productNameArabic ?? '';
  return `<tr>
      <td>${escapeHtml(nameAr || name)}${nameAr && name ? ` (${escapeHtml(name)})` : ''}</td>
      <td>${qty}</td>
      <td>${calcLUnitTotal(item)} د.أ</td>
      <td>${discountPercent.toFixed(2)}%</td>
      <td>${calcLineTotal(item)} د.أ</td>
    </tr>`;
};

const getCategoryLabel = (item) =>
  item.category?.nameArabic ?? item.category?.name ?? 'غير مصنف';

const getTrademarkLabel = (item) =>
  item.trademark?.nameArabic ?? item.trademark?.name ?? 'أخرى';

const buildGroupTitleHtml = (title, imageUrl, className) => {
  const resolvedUrl = imageUrl ? resolveImageUrl(imageUrl) : null;
  const imgHtml = resolvedUrl
    ? `<img src="${escapeHtml(resolvedUrl)}" alt="" class="group-title-img" />`
    : '';

  return `<div class="${className}">
    ${imgHtml}
    <span class="group-title-text">${escapeHtml(title)}</span>
  </div>`;
};

const groupItemsByCategoryAndTrademark = (items) => {
  const categoryOrder = [];
  const categories = new Map();

  items.forEach((item) => {
    const categoryKey = item.category?.id ?? getCategoryLabel(item);
    const categoryName = getCategoryLabel(item);

    if (!categories.has(categoryKey)) {
      categories.set(categoryKey, {
        name: categoryName,
        imageUrl: item.category?.imageUrl ?? null,
        trademarkOrder: [],
        trademarks: new Map(),
      });
      categoryOrder.push(categoryKey);
    }

    const categoryGroup = categories.get(categoryKey);
    const trademarkKey = item.trademark?.id ?? getTrademarkLabel(item);
    const trademarkName = getTrademarkLabel(item);

    if (!categoryGroup.trademarks.has(trademarkKey)) {
      categoryGroup.trademarks.set(trademarkKey, {
        name: trademarkName,
        imageUrl: item.trademark?.imageUrl ?? null,
        items: [],
      });
      categoryGroup.trademarkOrder.push(trademarkKey);
    }

    categoryGroup.trademarks.get(trademarkKey).items.push(item);
  });

  return categoryOrder.map((categoryKey) => {
    const categoryGroup = categories.get(categoryKey);
    return {
      name: categoryGroup.name,
      imageUrl: categoryGroup.imageUrl,
      trademarks: categoryGroup.trademarkOrder.map((trademarkKey) => ({
        name: categoryGroup.trademarks.get(trademarkKey).name,
        imageUrl: categoryGroup.trademarks.get(trademarkKey).imageUrl,
        items: categoryGroup.trademarks.get(trademarkKey).items,
      })),
    };
  });
};

const buildOrderItemsTableHead = () => `
  <thead>
    <tr>
      <th>المنتج</th>
      <th>الكمية</th>
      <th>سعر الوحدة</th>
      <th>الخصم</th>
      <th>الإجمالي</th>
    </tr>
  </thead>`;

const buildGroupedOrderItemsHtml = (items) => {
  if (!items.length) {
    return '<p class="empty-note"><em>لا توجد عناصر في هذا الطلب.</em></p>';
  }

  const categories = groupItemsByCategoryAndTrademark(items);

  return `<div class="items-section">${categories
    .map(
      ({ name: categoryName, imageUrl: categoryImageUrl, trademarks }) => `
    <div class="item-group">
      ${buildGroupTitleHtml(categoryName, categoryImageUrl, 'item-group-title')}
      <div class="item-group-body">
        ${trademarks
          .map(
            ({ name: trademarkName, imageUrl: trademarkImageUrl, items: trademarkItems }) => `
        <div class="trademark-group">
          ${buildGroupTitleHtml(trademarkName, trademarkImageUrl, 'trademark-group-title')}
          <table>
            ${buildOrderItemsTableHead()}
            <tbody>${trademarkItems.map(orderItemRow).join('')}</tbody>
          </table>
        </div>`
          )
          .join('')}
      </div>
    </div>`
    )
    .join('')}</div>`;
};

const getOrderInvoiceNumber = (order) =>
  order?.invoiceNumber ?? order?.invoice_number ?? null;

const orderSection = (order) => {
  numberOfAllItems = 0;   // Reset for each order

  const itemsNote = buildGroupedOrderItemsHtml(order.items || []);
  
  const statusLabel = order.isApproved ? 'معتمد' : 'قيد الانتظار';
  const invoiceNumber = getOrderInvoiceNumber(order);
  const invoiceBadge = invoiceNumber != null
    ? `<div class="invoice-badge">رقم الفاتورة: ${escapeHtml(invoiceNumber)}</div>`
    : '';

  return `<div class="section">
    ${buildPrintHeader()}
    <div class="order-meta">
      <div class="meta-card">
        ${invoiceBadge}
        <div class="detail-item"><strong>المحل:</strong> ${escapeHtml(order.shop?.name || 'غير متوفر')}</div>
        <div class="detail-item"><strong>رمز المندوب:</strong> ${escapeHtml(order.shop?.sale?.code || 'غير متوفر')}</div>
        <div class="detail-item"><strong>المنطقة:</strong> ${escapeHtml(order.shop?.ammanArea?.name || 'غير متوفر')}</div>
      </div>
      <div class="meta-card">
        <div class="detail-item"><strong>المبلغ الإجمالي:</strong> ${escapeHtml(order.totalAmount)} د.أ</div>
        <div class="detail-item"><strong>الحالة:</strong> ${statusLabel}</div>
        <div class="detail-item"><strong>تاريخ الإنشاء:</strong> ${escapeHtml(formatPrintDate(order.createdAt))}</div>
        <div class="detail-item"><strong>تاريخ التسليم:</strong> ${escapeHtml(order.deliveryDate || 'غير محدد')}</div>
      </div>
    </div>
    ${itemsNote}
    <div class="order-summary">
      <div class="summary-box">
        <div class="summary-row">
          <span>عدد المنتجات</span>
          <span>${escapeHtml(numberOfAllItems)}</span>
        </div>
        <div class="summary-row">
          <span>عناصر الطلب</span>
          <span>${escapeHtml(order.items?.length || 0)}</span>
        </div>
        <div class="summary-row total-row">
          <span>المجموع الكلي</span>
          <span>${escapeHtml(order.totalAmount)} د.أ</span>
        </div>
      </div>
    </div>
  </div>`;
};

export const printOrders = (orders, title = 'طباعة الطلبات') => {
  if (!orders?.length) throw new Error('لا توجد طلبات للطباعة');
  printHtmlDocument(title, orders.map(orderSection).join(''));
};

const returnItemRow = (item) => {
  const name = '' ;//item.productName ?? item.product_name ?? 'غير متوفر';
  const nameAr = item.productNameArabic ?? item.product_name_arabic ?? '';
  const qty = item.quantity ?? 0;
  const returnedQty = item.returnedQuantity ?? item.returned_quantity ?? '—';
  const unitPrice = parseFloat(item.unitPrice ?? item.unit_price ?? 0).toFixed(2);
  return `<tr>
    <td>${escapeHtml(nameAr || name)}${nameAr && name ? ` (${escapeHtml(name)})` : ''}</td>
    <td>${qty}</td>
    <td>${returnedQty}</td>
    <td>${unitPrice} د.أ</td>
    <td>${escapeHtml(item.returnReason ?? item.return_reason ?? '—')}</td>
  </tr>`;
};

const returnInvoiceSection = (invoice, saleLabel = '') => {
  const items = invoice.items ?? invoice.returnInvoiceItems ?? [];
  const itemsHtml = items.map(returnItemRow).join('');
  const itemsNote =
    itemsHtml === ''
      ? '<p><em>لا توجد عناصر مرتجعة في هذه الفاتورة.</em></p>'
      : `<table>
    <thead>
      <tr>
        <th>المنتج</th>
        <th>الكمية</th>
        <th>الكمية المرتجعة</th>
        <th>سعر الوحدة</th>
        <th>سبب الإرجاع</th>
      </tr>
    </thead>
    <tbody>${itemsHtml}</tbody>
  </table>`;

  return `<div class="section">
    <div class="section-header">
      <h2>فاتورة مرتجع #${escapeHtml(String(invoice.id).substring(0, 8))}…</h2>
      ${saleLabel ? `<p>${escapeHtml(saleLabel)}</p>` : ''}
    </div>
    <div class="details">
      <div class="detail-item"><strong>المبلغ الإجمالي:</strong> ${parseFloat(invoice.totalAmount ?? 0).toFixed(2)} د.أ</div>
      <div class="detail-item"><strong>مبلغ الإرجاع:</strong> ${parseFloat(invoice.totalReturnAmount ?? 0).toFixed(2)} د.أ</div>
      <div class="detail-item"><strong>تاريخ الإنشاء:</strong> ${escapeHtml(formatPrintDate(invoice.createdAt))}</div>
      <div class="detail-item"><strong>ملاحظات:</strong> ${escapeHtml(invoice.notes || '—')}</div>
    </div>
    <h3>العناصر المرتجعة</h3>
    ${itemsNote}
    <div class="total">مبلغ الإرجاع: ${parseFloat(invoice.totalReturnAmount ?? 0).toFixed(2)} د.أ</div>
  </div>`;
};

export const printReturnInvoices = (invoices, saleLabel = '') => {
  if (!invoices?.length) throw new Error('لا توجد فواتير مرتجع للطباعة');
  printHtmlDocument(
    'طباعة فواتير المرتجع',
    invoices.map((inv) => returnInvoiceSection(inv, saleLabel)).join('')
  );
};
